import { useMemo } from "react";
import { useGetList, useGetOne } from "react-admin";
import type { DocumentTransformation, TransformationTemplate } from "@/types";

interface UseDocumentTransformationDataProps {
  documentId: string | undefined;
}

/**
 * Hook to fetch document transformation data and its associated template
 *
 * Handles:
 * - Fetching all transformations for a document
 * - Getting the most recent transformation (by created_datetime)
 * - Fetching the transformation template if one exists
 * - Managing loading states
 *
 * @param documentId - The ID of the document
 * @returns Transformation data, template, loading state, and previous transformation count
 */
export const useDocumentTransformationData = ({ documentId }: UseDocumentTransformationDataProps) => {
  // Fetch transformations for this document
  // NO expand=extracted_records (#6755).
  //
  // That parameter made this request return every extracted record, which is
  // how the review tab used to get its rows. At 497,711 records mgmt-api
  // hydrates and JSON-serialises ~547 MB for a single response and is
  // OOM-killed — the API goes down for every user, not just this reviewer
  // (#6826). Records now come from the windowed endpoint via
  // useTransformationRecords; counts come off processing_metadata.record_summary
  // on the row this request already returns.
  const { data: transformations, isLoading: isLoadingTransformations } = useGetList<DocumentTransformation>(
    `documents/${documentId}/document-transformations`,
    {},
    {
      enabled: !!documentId,
    }
  );

  // Get the most recent transformation by sorting on created_datetime
  const latestTransformation = useMemo<DocumentTransformation | null>(() => {
    if (!transformations || transformations.length === 0) return null;

    // Sort by created_datetime descending and return the first one
    const latest = [...transformations].sort((a, b) => {
      const dateA = new Date(a.created_datetime).getTime();
      const dateB = new Date(b.created_datetime).getTime();
      return dateB - dateA; // Descending order (newest first)
    })[0];

    // The absolute-index stamping that used to live here has moved to the
    // windowed fetch, which carries the server's record_index per row
    // (recordIndexing.stampAbsoluteIndicesFromWindow). Nothing stamps from
    // array position on this path any more, because this path no longer
    // carries records at all.
    return latest;
  }, [transformations]);

  // Count of previous transformations (excluding the latest)
  const previousTransformationsCount = useMemo(() => {
    return transformations ? Math.max(0, transformations.length - 1) : 0;
  }, [transformations]);

  // Determine if we should fetch a template
  const shouldFetchTemplate = !!latestTransformation?.transformation_template_id_ref;

  // Fetch the transformation template if one exists
  const {
    data: template,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useGetOne<TransformationTemplate>(
    "transformation-templates",
    { id: latestTransformation?.transformation_template_id_ref || "" },
    { enabled: shouldFetchTemplate }
  );

  // Overall loading state — don't block on template if it errored (e.g. backend schema mismatch)
  const isLoading = isLoadingTransformations || (shouldFetchTemplate && isLoadingTemplate && !templateError);

  return {
    latestTransformation,
    template,
    isLoading,
    previousTransformationsCount,
    hasTransformation: !!latestTransformation,
  };
};
